// Lib
import { isEmpty } from 'lodash/fp';

import randomString from '../../../../common/uid/randomString';
import { asRect } from '../../../../common/maths/geometry/rect';
import { asObject } from '../../../../common/utils/immutableHelper';
import { canvasElementSorter } from '../../../../common/elements/utils/elementSortUtils';
import { getBoundingRectInGridUnits } from '../../../canvas/dnd/canvasUtil';
import { isCommentThread } from '../../../../common/elements/utils/elementTypeUtils';
import getElementClipboardHtmlString from '../utils/getElementClipboardHtmlString';
import { saveToClipboard } from './clipboardActions';
import { ClipboardOperation } from './clipboardConstants';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { DataTransferTypes } from '../../../../common/media/mimeConstants';
import getGridSize from '../../../utils/grid/gridSizeSelector';
import { getMeasurementsMap } from '../../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { getSelectedElements } from '../../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingId } from '../../../element/selection/currentlyEditingSelector';
import { getCurrentModalElementId } from '../../../element/modal/elementModalCommonSelector';
import { getPageIdSelector } from '../../../reducers/sessionSelector';
import { Thunk } from '../../../types/reduxTypes';
import { getCurrentUser, ImMNCurrentUser } from '../../../user/currentUserSelector';
import { isStaff } from '../../../user/userUtil';
import getElementContentsString from '../utils/getElementContentsString';
import globalLogger from '../../../logger';
import { ElementType } from '../../../../common/elements/elementTypes';
import { getImageBlobFromDomElement } from '../utils/getElementImageBlob';
import { isFeatureEnabled } from '../../../../common/users/utils/userPropertyUtils';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';
import type { PlatformDetails } from '../../../../common/platform/platformTypes';
import { writeToNativeClipboard } from '../../../capacitor/utils/capacitorClipboardUtils';
import { isPlatformModernMobileApp } from '../../../platform/utils/platformDetailsUtils';

const logger = globalLogger.createChannel('clipboardUtils');

/** Checks whether text can be written from a context-menu action on this platform. */
export const isTextClipboardWriteSupported = (platformDetails: PlatformDetails): boolean =>
    isPlatformModernMobileApp(platformDetails) ||
    (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function');

/** Writes text through the native bridge or browser Clipboard API when available. */
export const writeTextToClipboard = async (text: string, platformDetails: PlatformDetails): Promise<void> => {
    if (isPlatformModernMobileApp(platformDetails)) return writeToNativeClipboard(text);
    if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') return;

    await navigator.clipboard.writeText(text);
};

type AddElementMimeDataToClipboardProps = {
    event: ClipboardEvent;
    elements: MNElement[];
};

const addElementHtmlDataToClipboard = ({ event, elements }: AddElementMimeDataToClipboardProps) => {
    if (isEmpty(elements)) return;

    let htmlStr = '';

    // Loop through each element and get its HTML representation to add to the clipboard
    for (const element of elements) {
        const elementHtmlStr = getElementClipboardHtmlString(element);

        if (!elementHtmlStr) continue;

        // Separate each element with an empty div
        htmlStr = `${htmlStr}${elementHtmlStr}<div></div>`;
    }

    if (isEmpty(htmlStr)) return;

    htmlStr = `<meta charSet='utf-8'>${htmlStr}`;

    event.clipboardData?.setData(DataTransferTypes.HTML, htmlStr);
};

const addElementTextDataToClipboard = ({ event, elements }: AddElementMimeDataToClipboardProps) => {
    if (isEmpty(elements)) return;

    const textStr = elements.map(getElementContentsString).filter(Boolean).join('\n');
    if (isEmpty(textStr)) return;

    event.clipboardData?.setData(DataTransferTypes.PLAIN_TEXT, textStr);
};

const addElementImageToClipboard = async ({ event, elements }: AddElementMimeDataToClipboardProps) => {
    const pngBlob = await getImageBlobFromDomElement(event, elements[0]);
    if (!pngBlob) return;

    try {
        const clipboardItem = new ClipboardItem({ ['image/png']: pngBlob! });
        await navigator.clipboard.write([clipboardItem]);
    } catch (error) {
        logger.error('Failed to write images to clipboard', error);
    }
};

type AddElementDataToClipboardProps = {
    event: ClipboardEvent;
    saveId: string;
    operation: ClipboardOperation;
    sessionId: string;
    elements: MNElement[];
    elementsBoundingRect: Rect;
    user: ImMNCurrentUser;
};

const supportsNativeImageCopy =
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.write === 'function' &&
    typeof window.ClipboardItem === 'function';

const isSelectionCopyableImage = (elements: MNElement[]) => {
    if (!supportsNativeImageCopy) return false;
    if (elements.length > 1) return false;

    return elements[0].elementType === ElementType.IMAGE_TYPE;
};

const addElementDataToClipboard = ({
    event,
    saveId,
    operation,
    sessionId,
    elements,
    elementsBoundingRect,
    user,
}: AddElementDataToClipboardProps) => {
    // Our application "elements" data type
    const dataString = JSON.stringify({ saveId, operation, sessionId, elements, elementsBoundingRect });
    event.clipboardData?.setData(DataTransferTypes.ELEMENTS, dataString);

    // If the elements array contains only one element, the element is an image, and the browser
    // supports it, then save the image to the clipboard
    // NOTE: This functionality is currently experimental and behind a feature flag
    if (isFeatureEnabled(ExperimentId.copyImageData, user) && isSelectionCopyableImage(elements)) {
        addElementImageToClipboard({ event, elements });
        return;
    }

    // Save as HTML for other programs
    addElementHtmlDataToClipboard({ event, elements });

    // Save element content as text for other programs
    addElementTextDataToClipboard({ event, elements });
};

export const handleSaveToClipboardEventThunk =
    (event: ClipboardEvent, operation: ClipboardOperation): Thunk =>
    (dispatch, getState, { measurementsStore }) => {
        const state = getState();
        const gridSize = getGridSize(state);

        const selectedElements = getSelectedElements(state);
        const currentlyEditingId = getCurrentlyEditingId(state);
        const currentModalElementId = getCurrentModalElementId(state);

        if (!selectedElements.size || currentlyEditingId || currentModalElementId) return;

        // If only selecting a comment, let the user highlight and select comment text,
        // but only if they have actually selected text within the comment.
        // If no text is selected (selection is collapsed), proceed with the element clipboard operation.
        if (selectedElements.size === 1 && isCommentThread(selectedElements.first())) {
            const selection = window.getSelection();
            // If there's selected text (not collapsed), let the native copy/cut happen
            if (selection && !selection.isCollapsed) return;
        }

        event.preventDefault();

        const measurements = getMeasurementsMap(measurementsStore.getState());

        const saveId = randomString(16);
        const sessionId = getPageIdSelector(state);
        const elements = asObject(selectedElements.sort(canvasElementSorter)) as MNElement[];
        const elementsBoundingRect = asRect(
            getBoundingRectInGridUnits({
                elements,
                measurements,
                gridSize,
            }),
        );

        // Annotate copied data with measurements. This is just for exporting to the marketing site
        // animation tool and is meaningless otherwise, so only do this if the experiment is enabled.
        const user = getCurrentUser(state);
        if (isStaff(user)) {
            (elements as (MNElement & { measurements: Rect })[]).forEach((element) => {
                element.measurements = asObject(measurements.get(element.id!));
            });
        }

        addElementDataToClipboard({ event, saveId, elements, elementsBoundingRect, operation, sessionId, user });

        // On paste start offset at original location (index of -1)
        const pasteCount = operation === ClipboardOperation.cut ? -1 : 0;
        dispatch(saveToClipboard({ operation, elements, saveId, pasteCount, elementsBoundingRect }));
    };
